const cities = [
  { name: "Pasadena", zips: "91101, 91103, 91104, 91105, 91106, 91107" },
  { name: "Arcadia", zips: "91006, 91007" },
  { name: "Monrovia", zips: "91016, 91017" },
  { name: "San Marino", zips: "91108" },
  { name: "Temple City", zips: "91780" },
  { name: "Alhambra", zips: "91801, 91802, 91803" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your Caltech lab, JPL office, or Old Town apartment in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function PasadenaLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Pasadena &amp; the San Gabriel Valley
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Pasadena, California occupies a unique position in the American intellectual and cultural landscape. Home to the California Institute of Technology and the Jet Propulsion Laboratory — both world-famous institutions of scientific research — Pasadena generates more international travel per capita than almost any other Southern California city its size. Caltech faculty regularly attend academic conferences in Europe and Asia. JPL engineers travel to partner institutions and mission-critical meetings worldwide. Graduate students pursue exchange programs, field research, and dissertation fieldwork on every continent. For this community, an expired or missing passport is not merely an inconvenience — it is a career-disrupting emergency.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Beyond the academic world, Pasadena is one of the San Gabriel Valley's most affluent communities. The South Lake Avenue corridor hosts financial professionals, healthcare executives, and business owners with active international travel calendars. Arcadia and San Marino — immediately adjacent communities — have significant Asian-American populations with close family ties to East Asia, creating steady demand for expedited passport services especially around Lunar New Year, Golden Week, and summer travel seasons.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Pasadena is also deeply connected to the Rose Parade and Rose Bowl culture, which attracts visitors from around the world each January. Many international guests — and the Pasadena residents who host them — hold passports that have gone unrenewed since the pandemic era, creating a predictable spike in expedited renewal demand each fall as families realize their documents are expired.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center was built for exactly the situations Pasadena travelers encounter: tight academic deadlines, last-minute conference invitations, urgent research trips, and family travel with no room for delay. The Los Angeles Passport Agency is approximately 10 miles from Old Town Pasadena — but getting a same-day appointment there is rarely guaranteed. We provide the reliable, predictable alternative: start online, ship overnight from Pasadena, and receive your passport within your selected timeframe.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Pasadena, CA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Pasadena & the San Gabriel Valley</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — ship overnight from Pasadena, Arcadia, or San Marino
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">No office visit required for most services</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Start online, follow your personalized checklist, and ship your documents overnight. We review everything before submission and keep you updated throughout the process.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in the San Gabriel Valley — start online, ship overnight, and we handle the rest. No need to drive downtown to the LA Passport Agency.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Los Angeles", href: "/los-angeles" },
                    { name: "Glendale", href: "/glendale" },
                    { name: "Burbank", href: "/burbank" },
                    { name: "San Diego", href: "/san-diego" },
                    { name: "Irvine", href: "/irvine" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Pasadena Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}